import { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import MaterialReactTable, {
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_FullScreenToggleButton
} from 'material-react-table';
import {
  Box,
  CircularProgress,
  Button,
  Avatar,
  AvatarGroup,
  IconButton,
  Tooltip,
  Badge as MuiBadge,
  styled,
  Chip
} from '@mui/material';
import {
  ViewColumnOutlined,
  Search as SearchIcon,
  Clear as ClearSearchIcon,
  FilterAlt as FilterAltIcon,
  Add as AddIcon,
  Edit as EditIcon,
  FilterAltOff as FilterAltOffIcon,
  LocalPrintshopOutlined as LocalPrintshopOutlinedIcon
} from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';
import { ErrorToast, successToast } from '../../../components/Toasts';
import { GetAgencies } from '../../../apis/Admin';
import { base_logo_url } from '../../../apis/constants';

// Styled components for status
const ActiveChip = styled(Chip)({
  backgroundColor: '#E8F5E9',
  color: '#2E7D32',
  fontWeight: 500,
  fontSize: '13px',
  height: '28px',
  '& .MuiChip-icon': {
    color: '#2E7D32',
    fontSize: '16px'
  }
});

const InActiveChip = styled(Chip)({
  backgroundColor: '#F5F5F5',
  color: '#9E9E9E',
  fontWeight: 500,
  fontSize: '13px',
  height: '28px'
});

export function BasicTreeData({ PreviouProps }) {
  const [shipmentsLoader, setShipmentsLoader] = useState(false);
  const [agenciesDataArray, setAgenciesDataArray] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  const history = useHistory();
  const componentRef = useRef();

  const handleSuccessGetAgencies = ApiRes => {
    setShipmentsLoader(false);
    const ApiResAgencies = ApiRes?.data?.data;
    const tempArr = [];
    for (let index = 0; index < ApiResAgencies.length; index++) {
      const Item = ApiResAgencies[index];
      const Zip = ApiResAgencies[index].zipCode;
      const City = ApiResAgencies[index].city;
      const State = ApiResAgencies[index].state;
      const Country = ApiResAgencies[index].country;
      const AdminFirstName = ApiResAgencies[index].adminInfo?.firstName;
      const AdminLastName = ApiResAgencies[index].adminInfo?.lastName;

      const updatedArray = {
        ...Item,
        completeAddress: City + ' ' + State + ' ' + Zip + ' ' + Country,
        adminName: AdminFirstName + ' ' + AdminLastName
      };
      tempArr.push(updatedArray);
    }
    const filteredAgencies = tempArr.slice().reverse();
    setAgenciesDataArray(filteredAgencies);
  };

  useEffect(() => {
    setShipmentsLoader(true);
    GetAgencies()
      .then(res => res.status === 200 && handleSuccessGetAgencies(res))
      .catch(err => {
        setShipmentsLoader(false);
        ErrorToast(err?.message);
      });
  }, []);

  const handleCreateAgency = () => {
    history.push('/create-agency');
  };

  const handleEditAgency = agency => {
    history.push('create-agency', agency);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef?.current
  });

  const ConfiguredCarriers = ({ carriers }) => {
    const ArrayofCarriers = carriers;
    const ActiveAgencyCarriers =
      ArrayofCarriers?.filter(items => items.active) || [];

    if (ActiveAgencyCarriers.length === 0) {
      return (
        <span style={{ color: '#9E9E9E', fontSize: '13px' }}>No carriers</span>
      );
    }

    const visibleCarriers = ActiveAgencyCarriers.slice(0, 5);
    const remainingCount = ActiveAgencyCarriers.length - 5;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <AvatarGroup
          max={5}
          spacing='small'
          sx={{
            '& .MuiAvatar-root': {
              width: 40,
              height: 40,
              fontSize: '15px',
              border: '2px solid #FFFFFF',
              backgroundColor: '#F5F5F5'
            }
          }}
        >
          {visibleCarriers.map((items, index) => (
            <Tooltip key={index} title={items.name || 'Carrier'}>
              <Avatar
                src={items.icon ? base_logo_url + items.icon : ''}
                alt={items.name}
                sx={{
                  objectFit: 'contain',
                  backgroundColor: '#F5F5F5'
                }}
              >
                {!items.icon && (items.name?.charAt(0) || 'C')}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
        {remainingCount > 0 && (
          <Avatar
            sx={{
              width: 40,
              height: 40,
              fontSize: '15px',
              backgroundColor: '#E9ECEF',
              color: '#5F6B7A',
              fontWeight: 500,
              border: '2px solid #FFFFFF'
            }}
          >
            +{remainingCount}
          </Avatar>
        )}
      </div>
    );
  };

  // Define columns
  const userColumns = useMemo(
    () => [
      {
        accessorKey: 'actions',
        header: 'Actions',
        size: 80,
        enableColumnFilter: false,
        enableSorting: false,
        enableGlobalFilter: false,
        Cell: ({ row }) => {
          return (
            <Tooltip title='View / Update'>
              <IconButton
                onClick={e => {
                  e.stopPropagation(); // Prevent row click event
                  handleEditAgency(row.original);
                }}
                size='small'
                color='primary'
                sx={{ color: '#3361FF' }}
              >
                <svg
                  width='17'
                  height='17'
                  viewBox='0 0 13 13'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M1.33333 11.1693H2.26667L8.01667 5.41927L7.08333 4.48594L1.33333 10.2359V11.1693ZM10.8667 4.4526L8.03333 1.6526L8.96667 0.719271C9.22222 0.463715 9.53611 0.335938 9.90833 0.335938C10.2806 0.335938 10.5944 0.463715 10.85 0.719271L11.7833 1.6526C12.0389 1.90816 12.1722 2.21649 12.1833 2.5776C12.1944 2.93872 12.0722 3.24705 11.8167 3.5026L10.8667 4.4526ZM9.9 5.43594L2.83333 12.5026H0V9.66927L7.06667 2.6026L9.9 5.43594Z'
                    fill='#3361FF'
                  />
                </svg>
              </IconButton>
            </Tooltip>
          );
        }
      },
      {
        accessorKey: 'company',
        header: 'User Name',
        size: 200,
        enableSorting: true,
        Cell: ({ row }) => {
          const list = row.original;
          console.log(base_logo_url + list.logo, 'check images here');
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar
                src={base_logo_url + list.logo}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: '#6BA3E9',
                  fontSize: '16px'
                }}
              >
                {list.company?.charAt(0) || 'U'}
              </Avatar>
              <span style={{ fontWeight: 500, fontSize: '14px' }}>
                {list.company}
              </span>
            </div>
          );
        }
      },
      {
        accessorKey: 'adminInfo.phone',
        header: 'Phone Number',
        size: 150,
        enableSorting: true,
        Cell: ({ row }) => (
          <span style={{ color: '#1A73E8', fontSize: '14px' }}>
            {row.original.adminInfo?.phone || '-'}
          </span>
        )
      },
      {
        accessorKey: 'adminLoginId',
        header: 'Email',
        size: 250,
        enableSorting: true,
        Cell: ({ row }) => (
          <span style={{ color: '#1A73E8', fontSize: '14px' }}>
            {row.original.adminLoginId}
          </span>
        )
      },
      {
        accessorKey: 'role',
        header: 'Role',
        size: 150,
        enableSorting: true,
        Cell: ({ row }) => {
          let role = 'User';
          if (row.original.isAdmin) role = 'Admin';
          else if (row.original.isShippingManager) role = 'Shipping Manager';
          else if (row.original.isIT) role = 'IT';
          else if (row.original.isCustomerService) role = 'Customer Service';
          return <span style={{ fontSize: '14px' }}>{role}</span>;
        }
      },
      {
        accessorKey: 'active',
        header: 'Status',
        size: 120,
        enableSorting: true,
        Cell: ({ row }) => {
          if (row.original.active === true) {
            return (
              <ActiveChip icon={<span>✓</span>} label='ACTIVE' size='small' />
            );
          } else {
            return <InActiveChip label='INACTIVE' size='small' />;
          }
        }
      },
      {
        accessorKey: 'carriers',
        header: 'Carriers',
        size: 200,
        enableSorting: false,
        Cell: ({ row }) => {
          return <ConfiguredCarriers carriers={row.original.carriers} />;
        }
      }
    ],
    []
  );

  return (
    <div ref={componentRef}>
      <ToastContainer
        position='top-center'
        autoClose={5800}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <MaterialReactTable
        state={{
          isLoading: shipmentsLoader,
          globalFilter,
          columnFilters,
          sorting,
          pagination
          // showGlobalFilter: false
        }}
        onGlobalFilterChange={setGlobalFilter}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        onPaginationChange={setPagination}
        columns={userColumns}
        data={agenciesDataArray}
        enableRowSelection={false}
        enableSorting={true}
        muiTableBodyRowProps={({ row }) => ({
          sx: {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: '#F8F9FA'
            }
          }
        })}
        muiTableHeadCellFilterTextFieldProps={{ placeholder: '' }}
        muiTableContainerProps={{
          sx: {
            maxHeight: '70vh',
            minHeight: '70vh',
            overflow: 'auto'
          }
        }}
        initialState={{
          density: 'compact',
          // showGlobalFilter: false,
          pagination: {
            pageSize: 10,
            pageIndex: 0
          }
        }}
        positionToolbarAlertBanner='bottom'
        renderToolbarInternalActions={({ table }) => (
          <div>
            <MRT_ToggleGlobalFilterButton table={table} title='Search' />
            <IconButton
              title='Print'
              onClick={() => {
                handlePrint();
              }}
            >
              <LocalPrintshopOutlinedIcon />
            </IconButton>
            <MRT_ToggleFiltersButton table={table} title='Filters' />
            <MRT_ToggleDensePaddingButton table={table} title='Spacing' />
            <MRT_ShowHideColumnsButton
              table={table}
              title='Show/Hide Columns'
            />
            <MRT_FullScreenToggleButton table={table} title='Fullscreen' />
            <Tooltip title='Clear All Filters' arrow>
              <IconButton
                onClick={() => {
                  table.resetColumnFilters(true);
                  table.setShowColumnFilters(false);
                }}
              >
                <FilterAltOffIcon />
              </IconButton>
            </Tooltip>
          </div>
        )}
        enableRowActions={false}
        renderTopToolbarCustomActions={({ table }) => {
          return (
            <div>
              <Button
                size='small'
                onClick={handleCreateAgency}
                // className='m-2'
                style={{
                  color: '#3361FF',
                  backgroundColor: '#E4F3FD',
                  fontSize: '13px',
                  padding: '6px 16px'
                }}
                variant='contained'
              >
                Create Agency
                <svg
                  className='ms-2'
                  width='11'
                  height='10'
                  viewBox='0 0 11 10'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M4.94531 9.72396V5.72396H0.945312V4.39062H4.94531V0.390625H6.27865V4.39062H10.2786V5.72396H6.27865V9.72396H4.94531Z'
                    fill='#3361FF'
                  />
                </svg>
              </Button>
            </div>
          );
        }}
        muiTableProps={{
          sx: {
            borderCollapse: 'separate',
            borderSpacing: '0',
            tableLayout: 'fixed'
          }
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#5F6B7A',
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E9ECEF',
            whiteSpace: 'nowrap',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            padding: '14px 16px',
            '& .Mui-TableHeadCell-Content': {
              justifyContent: 'space-between'
            },
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: '#E9ECEF'
            }
          }
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '14px',
            color: '#2C3E50',
            borderBottom: '1px solid #F0F2F5',
            whiteSpace: 'nowrap',
            padding: '12px 16px'
          }
        }}
        muiTopToolbarProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E9ECEF',
            padding: '8px 16px'
          }
        }}
        muiBottomToolbarProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E9ECEF',
            padding: '8px 16px'
          }
        }}
        muiTablePaperProps={{
          sx: {
            boxShadow: 'none',
            border: '1px solid #E9ECEF',
            borderRadius: '8px',
            overflow: 'hidden'
          }
        }}
        enableStickyHeader={true}
        muiSearchTextFieldProps={{
          placeholder: 'Search',
          size: 'small',
          sx: {
            '& .MuiInputBase-root': {
              fontSize: '14px'
            }
          }
        }}
        localization={{
          noRecordsToDisplay: shipmentsLoader ? (
            <CircularProgress />
          ) : (
            'No Records Found'
          ),
          pagination: {
            labelRowsPerPage: 'Rows per page:',
            labelDisplayedRows: '{from}-{to} of {count}'
          }
        }}
      />
    </div>
  );
}
